import { Injectable, inject } from '@angular/core';
import { UsersService } from '@app/shared/services/entities/users.service';
import { RolesService } from '@app/shared/services/entities/roles.service';
import { ModulesService } from '@app/shared/services/modules.service';
import { PermissionsService } from '@app/shared/services/permissions.service';

/**
 * Permisos del usuario actual para un módulo, leídos de la BASE DE DATOS
 * (rol → accessPermissions → módulo → permisos LEER/CREAR/EDITAR/BORRAR).
 *
 * Es la misma fuente que usa el API para autorizar (utils/authorization.py),
 * así el front no muestra botones que el API va a rechazar. Antes había una
 * matriz fija por nombre de rol: cualquier rol nuevo (o renombrado) hacía
 * tronar la pantalla con "Cannot read properties of undefined".
 *
 * Banderas heredadas (shipping, reception, sign, invoice…) se mapean a EDITAR;
 * `read`/`detail` a LEER. El rol Administrador Epione tiene todo.
 *
 * Uso: `this.permissions = await this.rolePermissions.forRoute('/inventory');`
 */
export type ModuleFlags = Record<string, boolean>;

const ADMIN_ROLE = 'ADMINISTRADOR EPIONE';
const ALIASES: Record<string, string> = { VER: 'LEER', ELIMINAR: 'BORRAR', ACTUALIZAR: 'EDITAR' };
const READ_FLAGS = ['read', 'detail'];
const CREATE_FLAGS = ['create', 'add', 'request'];
const UPDATE_FLAGS = ['update', 'shipping', 'reception', 'authorize', 'return', 'sign', 'process', 'invoice', 'products'];
const DELETE_FLAGS = ['delete'];

function norm(value: unknown): string {
  return String(value ?? '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ').trim().toUpperCase();
}

function normRoute(route: unknown): string {
  return '/' + String(route ?? '').toLowerCase().split('?')[0].split('#')[0].replace(/^\/+|\/+$/g, '');
}

function flags(read: boolean, create: boolean, update: boolean, del: boolean): ModuleFlags {
  const out: ModuleFlags = {};
  READ_FLAGS.forEach(f => (out[f] = read));
  CREATE_FLAGS.forEach(f => (out[f] = create));
  UPDATE_FLAGS.forEach(f => (out[f] = update));
  DELETE_FLAGS.forEach(f => (out[f] = del));
  return out;
}

@Injectable({ providedIn: 'root' })
export class RolePermissionsService {
  private users = inject(UsersService);
  private roles = inject(RolesService);
  private modules = inject(ModulesService);
  private perms = inject(PermissionsService);

  private catalogs?: Promise<{ modules: any[]; permNames: Map<string, string> }>;

  /** Sin permisos: lo que se usa si algo falla o el usuario no tiene rol. */
  readonly none: ModuleFlags = flags(false, false, false, false);

  private loadCatalogs() {
    if (!this.catalogs) {
      this.catalogs = (async () => {
        const [m, p] = await Promise.all([
          this.modules.search({ searchType: 'default', condition: { deleted: false } }),
          this.perms.search({ searchType: 'default', condition: { deleted: false } }),
        ]);
        const permNames = new Map<string, string>(
          ((p as any).data ?? []).map((x: any) => {
            const n = norm(x.name);
            return [x.id, ALIASES[n] ?? n] as [string, string];
          }),
        );
        return { modules: ((m as any).data ?? []).filter((x: any) => x.active !== false), permNames };
      })().catch(err => { this.catalogs = undefined; throw err; });
    }
    return this.catalogs;
  }

  /**
   * Banderas del usuario actual para la ruta dada (ej. '/inventory',
   * '/reports/completed'). Usa el módulo con ruta exacta o, si no hay, el de
   * ruta más larga que sea prefijo. Si no existe módulo para `route`, prueba
   * las rutas de `fallbacks` en orden. Nunca lanza: si falla, regresa `none`.
   */
  async forRoute(route: string, ...fallbacks: string[]): Promise<ModuleFlags> {
    try {
      let user: any = this.users.currentUser();
      if (!user) user = await this.users.loadCurrentUser();
      if (!user?.role) return { ...this.none };

      const roleResp: any = await this.roles.search({ searchType: 'default', condition: { id: user.role } });
      const role = roleResp?.data?.[0];
      if (!role) return { ...this.none };
      if (norm(role.name) === ADMIN_ROLE) return flags(true, true, true, true);

      const { modules, permNames } = await this.loadCatalogs();
      const find = (route_: string) => {
        const target = normRoute(route_);
        return modules
          .map((m: any) => ({ m, r: normRoute(m.route) }))
          .filter(({ r }) => r !== '/' && (r === target || target.startsWith(r + '/')))
          .sort((a, b) => b.r.length - a.r.length)[0]?.m;
      };
      let module: any;
      for (const r of [route, ...fallbacks]) { module = find(r); if (module) break; }
      if (!module) return { ...this.none };

      const granted = new Set<string>(
        ((role.accessPermissions ?? {})[module.id] ?? [])
          .map((id: string) => permNames.get(id))
          .filter((n: string | undefined): n is string => !!n),
      );
      return flags(granted.has('LEER'), granted.has('CREAR'), granted.has('EDITAR'), granted.has('BORRAR'));
    } catch (err) {
      console.warn('[RolePermissions] No se pudieron leer los permisos:', err);
      return { ...this.none };
    }
  }
}
